import { DatePicker } from "antd";
import React, { useState, useEffect } from "react";
import { CalendarIcon } from "../svgs";
import "./CustomDatePicker.scss";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";

// using dayjs for easy date math
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localeData);

const CustomDatePicker = ({ onChange, value, format, ...rest }) => {
  // 2025-05-12: Must expressly use startOf("day") everywhere to avoid timezone issues
  const getTomorrowDate = () => {
    return dayjs().add(1, "day").startOf("day");
  };

  // Function to check if a date should be disabled
  const isDateDisabled = (date) => {
    // The only time we want to check specifically PR time is this logic
    const now = dayjs().tz("America/Puerto_Rico");
    const todayAt16PM = now.set("hour", 16).set("minute", 0).set("second", 0);

    // If current time is before 16 PM, allow today and future dates
    if (now.isBefore(todayAt16PM)) {
      return date && date < now.startOf("day");
    }
    // If current time is after 16 PM, only allow tomorrow and future dates
    else {
      return date && date < now.add(1, "day").startOf("day");
    }
  };

  // Initialize with tomorrow's date
  const initialDate = () => {
    const savedDateStr = localStorage.getItem("pickupDate");
    if (savedDateStr) {
      const savedDate = dayjs(savedDateStr).startOf("day");
      return isDateDisabled(savedDate) ? getTomorrowDate() : savedDate;
    }
    return value ? value.startOf("day") : getTomorrowDate();
  };

  // Use state to manage the date
  const [selectedDate, setSelectedDate] = useState(initialDate);

  // Create disabledDate function for the DatePicker
  const disabledDate = (current) => {
    if (!current) return false;
    return isDateDisabled(current);
  };

  // Effect to validate the date whenever the component mounts or when value changes
  useEffect(() => {
    // If an external value is provided, use it (but validate first)
    if (value) {
      const validValue = isDateDisabled(value) ? getTomorrowDate() : value;
      if (!selectedDate.isSame(validValue, "day")) {
        setSelectedDate(validValue);
        // 2026-02-12: Propagate corrected date back to parent when the incoming value
        // is disabled/stale. Without this, the parent state can hold a stale date while
        // the DatePicker internally shows the corrected one.
        if (onChange && isDateDisabled(value)) {
          onChange(validValue);
          localStorage.setItem("pickupDate", validValue.format("YYYY-MM-DD"));
        }
      }
    }

    // Setup a timer to check periodically (especially important around 4 PM)
    const timer = setInterval(() => {
      const now = dayjs();
      const todayAt16PM = now.set("hour", 16).set("minute", 0).set("second", 0);

      // If it's after 4 PM and the selected date is today, update to tomorrow
      if (now.isAfter(todayAt16PM) && selectedDate.isSame(now, "day")) {
        const tomorrow = getTomorrowDate();
        setSelectedDate(tomorrow);
        if (onChange) {
          onChange(tomorrow);
        }
        localStorage.setItem("pickupDate", tomorrow.format("YYYY-MM-DD"));
      }
    }, 15000); // Check every 15s

    return () => clearInterval(timer);
  }, [value, selectedDate, onChange]);

  const handleChange = (date) => {
    if (!date) return;

    // Ensure the date is in the correct timezone and at start of day
    const adjustedDate = date.startOf("day");

    setSelectedDate(adjustedDate);
    localStorage.setItem("pickupDate", adjustedDate.format("YYYY-MM-DD"));

    if (onChange) {
      // Important: Clone the date object to prevent reference issues
      onChange(adjustedDate.clone());
    }
  };

  return (
    <DatePicker
      allowClear={false}
      suffixIcon={<CalendarIcon />}
      className="custom-date-picker"
      disabledDate={disabledDate}
      defaultPickerValue={getTomorrowDate()}
      value={selectedDate}
      onChange={handleChange}
      format={format || "YYYY-MM-DD"}
      {...rest}
    />
  );
};

export default CustomDatePicker;
